import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {mapBasicDataItemsToSelectItems} from '@/modules/basic-data/functions'
import {
  durationUnitOptions,
  expiryRuleOptions,
  microbiologicalLimitOptions,
  stabilityStageOptions,
} from '@/modules/drug-catalog/constants'
import {useGetInfiniteSolutionDrugs} from '@/modules/drug-catalog/solutions/api'
import {
  showStorageTemperatureFields,
  StabilityRuleFormFieldsOptions,
  StorageConditionsContentMap,
  storageConditionsFields,
} from '@/modules/drug-catalog/stability-rules/constants'
import {useGetInfiniteActiveSubstanceDrugs} from '@/modules/drug-catalog/substances/api'
import {Box, Button, Grid, HStack} from '@chakra-ui/react'
import {BasicDataResources} from '@cypro/engine/schema'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {withForm} from '@cypro/ui/integrations/form'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'

export const StabilityRuleFormFields = withForm({
  ...StabilityRuleFormFieldsOptions,
  render: function Render({form, onCancel}) {
    const {t} = useTranslate('common')
    const {t: tStabilityRules} = useTranslate('stability-rules')

    const {items: containerTypes} = useGetBasicDataItems({resource: BasicDataResources.containerTypes})
    const {items: containerMaterials} = useGetBasicDataItems({resource: BasicDataResources.containerMaterials})
    const {items: storageLabels} = useGetBasicDataItems({resource: BasicDataResources.storageLabels})
    const activeSubstanceDrugs = useGetInfiniteActiveSubstanceDrugs()
    const solutionDrugs = useGetInfiniteSolutionDrugs()

    const containerTypeSelectItems = mapBasicDataItemsToSelectItems(containerTypes)
    const containerMaterialSelectItems = mapBasicDataItemsToSelectItems(containerMaterials)
    const storageLabelSelectItems = mapBasicDataItemsToSelectItems(storageLabels, {includeCode: false})

    const {
      applicabilityConditions,
      stabilityStageSelectItems,
      durationUnitSelectItems,
      expiryRuleSelectItems,
      microbiologicalLimitSelectItems,
    } = useMemo(() => {
      const applicabilityConditions = [
        {name: 'singleDoseOnly', label: t('single_dose_only')},
        {name: 'preservativePresent', label: t('filter_required')},
        {name: 'requiresAsepticHandling', label: t('requires_aseptic_handling')},
      ] as const

      const stabilityStageSelectItems = stabilityStageOptions.map(item => ({label: t(item), value: item}))
      const durationUnitSelectItems = durationUnitOptions.map(item => ({label: t(item), value: item}))
      const expiryRuleSelectItems = expiryRuleOptions.map(item => ({label: t(item), value: item}))
      const microbiologicalLimitSelectItems = microbiologicalLimitOptions.map(item => ({label: t(item), value: item}))

      return {
        applicabilityConditions,
        stabilityStageSelectItems,
        durationUnitSelectItems,
        expiryRuleSelectItems,
        microbiologicalLimitSelectItems,
      }
    }, [t])

    return (
      <>
        <form.ErrorObserver />

        <Box flex="1" height="100%" overflowY="auto">
          <Grid gridTemplateColumns="1fr 1fr" gapX="4" gapY="4">
            <FormSection
              label={t('trigger_and_stage')}
              description={tStabilityRules('trigger_and_stage_description')}
              variant="card"
            >
              <form.AppField name="stabilityStage">
                {field => (
                  <field.Select
                    label={t('stability_stage')}
                    placeholder={t('eg', {value: stabilityStageSelectItems[0]?.label})}
                    items={stabilityStageSelectItems}
                    required
                  />
                )}
              </form.AppField>

              <form.AppField name="startEventNotes">
                {field => (
                  <field.Textarea
                    label={t('start_event_notes')}
                    placeholder={tStabilityRules('start_event_notes_placeholder')}
                    rows={3}
                  />
                )}
              </form.AppField>
            </FormSection>

            <FormSection
              label={t('time_limits')}
              description={tStabilityRules('time_limits_description')}
              variant="card"
            >
              <Grid gridTemplateColumns="1fr 1fr" gapX="4" gapY="4">
                <form.AppField name="durationValue">
                  {field => (
                    <field.NumberInput
                      label={t('stability_duration_value')}
                      placeholder={tStabilityRules('stability_duration_value_placeholder')}
                      min={0}
                      required
                    />
                  )}
                </form.AppField>
                <form.AppField name="durationUnit">
                  {field => (
                    <field.Select
                      label={t('duration_unit')}
                      items={durationUnitSelectItems}
                      placeholder={t('eg', {value: durationUnitSelectItems[0]?.label})}
                      required
                    />
                  )}
                </form.AppField>

                <form.AppField name="expiryRuleType">
                  {field => (
                    <field.Select
                      label={t('expiry_rule')}
                      items={expiryRuleSelectItems}
                      placeholder={t('eg', {value: expiryRuleSelectItems[0]?.label})}
                      required
                    />
                  )}
                </form.AppField>

                <form.AppField name="microbiologicalLimitation">
                  {field => (
                    <field.Select
                      label={t('microbiological_limit')}
                      items={microbiologicalLimitSelectItems}
                      placeholder={t('eg', {value: microbiologicalLimitSelectItems[0]?.label})}
                    />
                  )}
                </form.AppField>
              </Grid>

              <form.AppField name="discardRemainderRequired">
                {field => <field.CheckBox label={t('discard_remainder_required')} size="sm" />}
              </form.AppField>
            </FormSection>

            <FormSection
              label={t('storage_conditions')}
              description={tStabilityRules('storage_conditions_description')}
              variant="card"
            >
              <form.AppField name="storageLabelId">
                {field => (
                  <field.Select
                    label={t('storage_condition')}
                    placeholder={t('eg', {value: storageLabelSelectItems[0]?.label})}
                    items={storageLabelSelectItems}
                    required
                  />
                )}
              </form.AppField>

              {showStorageTemperatureFields && (
                <Grid gridTemplateColumns="1fr 1fr" gapX="4">
                  <form.AppField name="tempMinC">
                    {field => (
                      <field.NumberInput
                        label={t('min_temp_c')}
                        placeholder={tStabilityRules('min_temp_c_placeholder')}
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="tempMaxC">
                    {field => (
                      <field.NumberInput
                        label={t('max_temp_c')}
                        placeholder={tStabilityRules('max_temp_c_placeholder')}
                      />
                    )}
                  </form.AppField>
                </Grid>
              )}

              <Grid gridTemplateColumns="1fr 1fr" gapX="4" gapY="2">
                {storageConditionsFields.map(name => (
                  <form.AppField key={name} name={name}>
                    {field => <field.CheckBox label={t(StorageConditionsContentMap[name].label)} size="sm" />}
                  </form.AppField>
                ))}
              </Grid>
            </FormSection>

            <FormSection
              label={t('applicability_and_constraints')}
              description={tStabilityRules('applicability_and_constraints_description')}
              variant="card"
            >
              <Grid gridTemplateColumns="1fr 1fr" gapX="4" gapY="4">
                <form.AppField name="carrierDrugId">
                  {field => (
                    <field.InfiniteCombobox
                      label={t('carrier_solution')}
                      placeholder={t('eg', {value: t('name')})}
                      {...solutionDrugs}
                    />
                  )}
                </form.AppField>

                <form.AppField name="additionalActiveSubstanceDrugId">
                  {field => (
                    <field.InfiniteCombobox
                      label={t('additional_active_substance')}
                      placeholder={t('eg', {value: t('name')})}
                      {...activeSubstanceDrugs}
                    />
                  )}
                </form.AppField>

                <form.AppField name="containerTypeId">
                  {field => (
                    <field.Select
                      label={t('container_type')}
                      items={containerTypeSelectItems}
                      placeholder={t('eg', {value: containerTypeSelectItems[0]?.label})}
                    />
                  )}
                </form.AppField>

                <form.AppField name="containerMaterialsIds">
                  {field => (
                    <field.Select
                      label={t('material_restrictions')}
                      items={containerMaterialSelectItems}
                      placeholder={t('eg', {value: containerMaterialSelectItems[0]?.label})}
                      multiple
                    />
                  )}
                </form.AppField>
              </Grid>

              <Grid gridTemplateColumns="1fr 1fr" gapX="4" gapY="2">
                {applicabilityConditions.map(cond => (
                  <form.AppField key={cond.name} name={cond.name}>
                    {field => <field.CheckBox label={cond.label} size="sm" />}
                  </form.AppField>
                ))}
              </Grid>
            </FormSection>
          </Grid>

          <Box mt="4">
            <form.AppField name="labelCopies">
              {field => (
                <field.NumberInput
                  label={tStabilityRules('label_copies')}
                  description={tStabilityRules('label_copies_description')}
                  min={1}
                />
              )}
            </form.AppField>
          </Box>

          <Box mt="4">
            <form.AppField name="notes">
              {field => (
                <field.Textarea label={t('general_notes')} placeholder={tStabilityRules('general_notes_description')} />
              )}
            </form.AppField>
          </Box>
        </Box>

        <HStack gapX="3" justifyContent="space-between" pt="4">
          <form.AppField name="defaultRule">
            {field => <field.Switch label={t('set_as_default_rule')} colorPalette="green" />}
          </form.AppField>

          <HStack gapX="3">
            <Button variant="outline" onClick={onCancel}>
              {t('cancel')}
            </Button>
            <form.SubmitButton>{t('save')}</form.SubmitButton>
          </HStack>
        </HStack>
      </>
    )
  },
})
